import { googleLogout } from '@react-oauth/google';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import NoticeMessage from '@/plugin/noticemessage/noticemessage.js';
import type { NavbarItem } from '@/shared/components/layout/Sidebar/Sidebar.types';
import { APIS, URLS } from '@/shared/config/urls';
import { DEFAULT_LANDING_ROUTE } from '@/shared/utils/loginRedirect';
import { request } from '@/shared/utils/request';
import { useAuthStore } from '@/store/authStore';
import { passwordLogin } from '../apis/auth';
import type {
  CheckStatusResponse,
  ForgotPasswordRequest,
  GoogleSignInRequest,
  PasswordLoginRequest,
  SessionProfile,
} from '../types/auth.types';

const useAuth = ({
  userID = null,
  onLoginFail = (_err?: unknown) => {},
  onResetFail = (_err?: unknown) => {},
  // onSuccessUpdate = () => {},
}: {
  userID?: string | null;
  onLoginFail?: (err?: unknown) => void;
  onResetFail?: (err?: unknown) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Setelah login tanpa halaman asal (?redirect=), user diarahkan ke halaman
  // utama toko (/store/home).
  const resolveLandingRoute = async (): Promise<string> => DEFAULT_LANDING_ROUTE;

  // credential = Google ID token; the backend verifies it, links or creates
  // the account and answers with the session cookie + profile. redirectTo =
  // the screen that sent the user to the login page (already validated).
  const handleGoogleLogin = async (credential: string, redirectTo?: string) => {
    const requestURL = `${URLS.BACK_DSH}${APIS.googleLogin}`;
    const body: GoogleSignInRequest = { credential };
    return request
      .post<SessionProfile>(requestURL, body)
      .then((profile) => onSignedIn(profile, redirectTo))
      .catch((err) => {
        onLoginFail(err);
      });
  };

  // Username or email + password
  const handlePasswordLogin = async (body: PasswordLoginRequest, redirectTo?: string) => {
    return passwordLogin(body)
      .then((profile) => onSignedIn(profile, redirectTo))
      .catch((err) => {
        onLoginFail(err);
      });
  };

  const onSignedIn = async (profile: SessionProfile, redirectTo?: string) => {
    useAuthStore.getState().setAuth(profile);
    queryClient.clear();
    navigate(redirectTo ?? (await resolveLandingRoute()), { replace: true });
  };

  const handleLogout = async () => {
    const requestURL = `${URLS.BACK_DSH}${APIS.logout}`;
    return request.get(requestURL).then(() => {
      // Stops Google One Tap from signing the same account in again automatically
      googleLogout();
      useAuthStore.getState().sendLogoutSignal();
      useAuthStore.getState().clearAuth();
      queryClient.clear();

      navigate('/');
    });
  };

  const { data: handleAuthority } = useQuery({
    queryKey: ['navbarList'],
    queryFn: async () => {
      // const requestURL = `${URLS.BACK_DSH}${APIS.accessMenu}`;
      // const response = await request.get(requestURL);
      // return response;
      const requestURL = `${URLS.BACK_DSH}${APIS.accessMenu}`;
      return request.get<NavbarItem[]>(requestURL);
    },
    enabled: !!userID,
    staleTime: 1000 * 60 * 1,
  });
  // console.log(handleAuthority);

  const getIsLogin = () => {
    return useAuthStore.getState().isLogin;
  };

  const handleForgotPassword = async (accountDT: ForgotPasswordRequest) => {
    const requestURL = `${URLS.BACK_DSH}${APIS.forgotPassword}`;
    return request
      .post(requestURL, accountDT)
      .then((_data) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(`성공적으로 업데이트하였습니다.`, {
          callback() {
            location.reload();
          },
        });
      })
      .catch((err) => {
        onResetFail(err);
      });
  };

  const checkStatus = async () => {
    request
      .get<CheckStatusResponse>(`${URLS.BACK_DSH}${APIS.checkStatus}`)
      .then((response) => {
        if (response.ok === false) {
          useAuthStore.getState().clearAuth();
          queryClient.clear();
          navigate('/');
          return;
        }
        // Refresh the persisted copy (tier, group, picture may have changed)
        useAuthStore.getState().setAuth(response);
      })
      .catch(() => {
        // 401 is also handled by the axios response interceptor in request.ts
        useAuthStore.getState().clearAuth();
        queryClient.clear();
        navigate('/');
      });
  };

  return {
    checkStatus,
    getIsLogin,
    handleGoogleLogin,
    handlePasswordLogin,
    handleLogout,
    resolveLandingRoute,
    handleForgotPassword,
    handleAuthority,
  };
};

export default useAuth;
